import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter 
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { 
  Plus, MagnifyingGlass, Pencil, Trash, User, Phone, 
  EnvelopeSimple, MapPin, Eye, Download, Upload
} from '@phosphor-icons/react';
import { toast } from 'sonner';

import { API } from '../config/api';

const statusColors = {
  lead: 'bg-blue-100 text-blue-800',
  quote_sent: 'bg-yellow-100 text-yellow-800',
  won: 'bg-green-100 text-green-800',
  lost: 'bg-red-100 text-red-800'
};

export default function ClientsPage() {
  const { t } = useTranslation();

  const statusLabels = {
    lead: t('clients.statusLabels.lead'),
    quote_sent: t('clients.statusLabels.quote_sent'),
    won: t('clients.statusLabels.won'),
    lost: t('clients.statusLabels.lost')
  };

  const pipelineLabels = {
    new_lead: t('clients.pipelineLabels.new_lead'),
    contacted: t('clients.pipelineLabels.contacted'),
    site_visit: t('clients.pipelineLabels.site_visit'),
    quote_sent: t('clients.pipelineLabels.quote_sent'),
    negotiation: t('clients.pipelineLabels.negotiation'),
    won: t('clients.pipelineLabels.won'),
    lost: t('clients.pipelineLabels.lost')
  };
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    cantiere: '',
    notes: '',
    status: 'lead',
    pipeline_stage: 'new_lead'
  });
  const navigate = useNavigate();

  const fetchClients = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      
      const { data } = await axios.get(`${API}/clients?${params}`);
      setClients(Array.isArray(data) ? data : [])
    } catch (err) {
      console.error('Error fetching clients:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, [search, statusFilter]);

  const openModal = (client = null) => {
    if (client) {
      setEditingClient(client);
      setFormData({
        name: client.name || '',
        phone: client.phone || '',
        email: client.email || '',
        address: client.address || '',
        cantiere: client.cantiere || '',
        notes: client.notes || '',
        status: client.status || 'lead',
        pipeline_stage: client.pipeline_stage || 'new_lead'
      });
    } else {
      setEditingClient(null);
      setFormData({
        name: '',
        phone: '',
        email: '',
        address: '',
        cantiere: '',
        notes: '',
        status: 'lead',
        pipeline_stage: 'new_lead'
      });
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingClient) {
        await axios.put(`${API}/clients/${editingClient.id}`, formData);
      } else {
        await axios.post(`${API}/clients`, formData);
      }
      setIsModalOpen(false);
      fetchClients();
    } catch (err) {
      console.error('Error saving client:', err);
    }
  };

  const handleDelete = async (clientId) => {
    if (!window.confirm(i18n.language === 'es' ? 'Eliminar este cliente?' : 'Sei sicuro di voler eliminare questo cliente?')) return;
    try {
      await axios.delete(`${API}/clients/${clientId}`);
      fetchClients();
    } catch (err) {
      console.error('Error deleting client:', err);
    }
  };

  // CSV Export
  const exportClientsCsv = () => {
    const headers = ['name', 'email', 'phone', 'address', 'notes', 'status', 'pipeline_stage'];
    const csvRows = [headers.join(',')];
    (clients || []).forEach(c => {
      csvRows.push(headers.map(h => `"${(c[h] || '').toString().replace(/"/g, '""')}"`).join(','));
    });
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'clienti.csv'; a.click();
    URL.revokeObjectURL(url);
    toast.success(t('clients.exportSuccess'));
  };

  // CSV Import
  const importClientsCsv = () => {
    const input = document.createElement('input');
    input.type = 'file'; input.accept = '.csv';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      const text = await file.text();
      const lines = text.trim().split('\n');
      if (lines.length < 2) { toast.error(t('csv.invalidFormat')); return; }
      const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
      let imported = 0;
      for (let i = 1; i < lines.length; i++) {
        const vals = lines[i].match(/(".*?"|[^,]*),?/g)?.map(v => v.replace(/,$/,'').replace(/^"|"$/g, '').replace(/""/g, '"')) || [];
        const row = {};
        headers.forEach((h, idx) => { row[h] = vals[idx] || ''; });
        if (row.name) {
          try {
            await axios.post(`${API}/clients`, { name: row.name, email: row.email || '', phone: row.phone || '', address: row.address || '', notes: row.notes || '', status: row.status || 'lead', pipeline_stage: row.pipeline_stage || 'new_lead' });
            imported++;
          } catch (err) { /* skip invalid rows */ }
        }
      }
      toast.success(t('clients.importSuccess', { count: imported }));
      fetchClients();
    };
    input.click();
  };

  return (
    <div className="space-y-6" data-testid="clients-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-outfit text-2xl sm:text-3xl font-bold text-[#0A0F1A]">
            {t('clients.title')}
          </h1>
          <p className="text-[#4A5568] mt-1">
            {t('clients.subtitle')}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportClientsCsv} className="border-[#0033A0] text-[#0033A0]" data-testid="export-clients-btn">
            <Download size={18} className="mr-1" /> {t('common.export')} CSV
          </Button>
          <Button variant="outline" onClick={importClientsCsv} className="border-[#0033A0] text-[#0033A0]" data-testid="import-clients-btn">
            <Upload size={18} className="mr-1" /> {t('common.import')} CSV
          </Button>
          <Button
            onClick={() => openModal()}
            className="bg-[#0033A0] hover:bg-[#0047AB] text-white"
            data-testid="add-client-button"
          >
            <Plus size={20} className="mr-2" />
            {t('clients.newClient')}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5568]" size={20} />
          <Input
            placeholder={t('clients.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 border-[#E2E8F0]"
            data-testid="client-search-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-48 border-[#E2E8F0]" data-testid="status-filter">
            <SelectValue placeholder="Tutti gli stati" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati</SelectItem>
            {Object.entries(statusLabels).map(([value, label]) => (
              <SelectItem key={value} value={value}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Clients Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0033A0]"></div>
        </div>
      ) : (clients || []).length === 0 ? (
        <div className="text-center py-12 bg-[#F4F6F8] rounded-sm border border-[#E2E8F0]">
          <User size={48} className="mx-auto text-[#4A5568] mb-4" />
          <h3 className="font-medium text-[#0A0F1A] mb-2">Nessun cliente trovato</h3>
          <p className="text-sm text-[#4A5568] mb-4">
            Inizia aggiungendo il tuo primo cliente
          </p>
          <Button
            onClick={() => openModal()}
            className="bg-[#0033A0] hover:bg-[#0047AB] text-white"
          >
            <Plus size={20} className="mr-2" />
            Aggiungi Cliente
          </Button>
        </div>
      ) : (
        <div className="border border-[#E2E8F0] rounded-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#F4F6F8]">
                <tr>
                  <th className="px-4 py-3 text-left text-xs uppercase tracking-wider font-bold text-[#4A5568]">
                    {t('quotes.client')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs uppercase tracking-wider font-bold text-[#4A5568] hidden md:table-cell">
                    {t('common.phone')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs uppercase tracking-wider font-bold text-[#4A5568] hidden lg:table-cell">
                    {t('common.address')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs uppercase tracking-wider font-bold text-[#4A5568]">
                    {t('common.status')}
                  </th>
                  <th className="px-4 py-3 text-right text-xs uppercase tracking-wider font-bold text-[#4A5568]">
                    {t('common.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-[#E2E8F0]">
                {(clients || []).map((client) => (
                  <tr key={client.id} className="hover:bg-[#F4F6F8] transition-colors" data-testid={`client-row-${client.id}`}>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#0033A0]/10 flex items-center justify-center">
                          <User size={20} className="text-[#0033A0]" />
                        </div>
                        <div>
                          <p className="font-medium text-[#0A0F1A] hover:text-[#0033A0] cursor-pointer" onClick={() => window.location.href = `/clients/${client.id}`}>{client.name}</p>
                          <p className="text-xs text-[#4A5568]">{pipelineLabels[client.pipeline_stage]}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 hidden md:table-cell">
                      <div className="space-y-1">
                        {client.email && (
                          <div className="flex items-center gap-2 text-sm text-[#4A5568]">
                            <EnvelopeSimple size={14} />
                            {client.email}
                          </div>
                        )}
                        {client.phone && (
                          <div className="flex items-center gap-2 text-sm text-[#4A5568]">
                            <Phone size={14} />
                            {client.phone}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 hidden lg:table-cell">
                      {client.address && (
                        <div className="flex items-center gap-2 text-sm text-[#4A5568]">
                          <MapPin size={14} />
                          <span className="truncate max-w-xs">{client.address}</span>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <span className={`px-2 py-1 rounded-sm text-xs font-medium ${statusColors[client.status]}`}>
                        {statusLabels[client.status]}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/quotes/new?client=${client.id}`)}
                          className="text-[#0033A0] hover:bg-[#0033A0]/10"
                          data-testid={`create-quote-${client.id}`}
                        >
                          <Eye size={18} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openModal(client)}
                          className="text-[#4A5568] hover:text-[#0A0F1A]"
                          data-testid={`edit-client-${client.id}`}
                        >
                          <Pencil size={18} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(client.id)}
                          className="text-red-600 hover:bg-red-50"
                          data-testid={`delete-client-${client.id}`}
                        >
                          <Trash size={18} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl">
              {editingClient ? t('clients.editClient') : t('clients.newClient')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">{t('common.name')} *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="mt-1"
                data-testid="client-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="phone">{t('common.phone')}</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="mt-1"
                  data-testid="client-phone-input"
                />
              </div>
              <div>
                <Label htmlFor="email">{t('common.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="mt-1"
                  data-testid="client-email-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="address">{t('common.address')}</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="mt-1"
                data-testid="client-address-input"
              />
            </div>
            <div>
              <Label htmlFor="cantiere" className="flex items-center gap-2">
                Indirizzo cantiere
                <span className="text-[10px] text-[#A0AEC0] font-normal">(se diverso dall'indirizzo cliente)</span>
              </Label>
              <Input
                id="cantiere"
                value={formData.cantiere}
                onChange={(e) => setFormData({ ...formData, cantiere: e.target.value })}
                className="mt-1"
                placeholder="Es. Via del mare 5, 50100 Firenze (casa al mare)"
                data-testid="client-cantiere-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="status">{t('common.status')}</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                  <SelectTrigger className="mt-1" data-testid="client-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(statusLabels).map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="pipeline">{t('clients.pipelineStage')}</Label>
                <Select value={formData.pipeline_stage} onValueChange={(v) => setFormData({ ...formData, pipeline_stage: v })}>
                  <SelectTrigger className="mt-1" data-testid="client-pipeline-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(pipelineLabels).map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="notes">{t('common.notes')}</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="mt-1"
                rows={3}
                data-testid="client-notes-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" className="bg-[#0033A0] hover:bg-[#0047AB] text-white" data-testid="save-client-button">
                {editingClient ? t('common.save') : t('common.create')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

